import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, Checkbox, Grid, LinearProgress, MenuItem, TextField, Typography, styled } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { NavLink, useNavigate } from 'react-router-dom';
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-google-places-autocomplete';
import axios from '../../../axios';
import logo from '../../../assets/logo.png';
import useAuth from '../../hooks/useAuth';
import { DIRECT_CUSTOMER_SIGNUP, PUBLIC_COMPANIES } from '../../api';
import { saveActiveProvider } from '../../utils/tenantApi';

const SignupShell = styled(Box)(() => ({
  height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A2038',
  '& .signup-card': { width: '100%', maxWidth: 860, height: 'calc(100vh - 32px)', maxHeight: 720, margin: 16, borderRadius: 12, overflow: 'hidden' },
  '& .logo': { width: 180, marginBottom: 18 },
}));

const initialAccount = { email: '', password: '', confirmPassword: '', remember: true };
const initialProfile = { first_name: '', last_name: '', phone_number: '', address: '', city: '', state: '', zip_code: '', country: 'United States', gender: 'Unspecified', language: 'english', latitude: '', longitude: '' };
const accountSchema = Yup.object({
  email: Yup.string().email('Enter a valid email address').required('Email is required'),
  password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('Confirm password is required'),
  remember: Yup.boolean().oneOf([true], 'You must accept terms and policy'),
});
const profileSchema = Yup.object({
  first_name: Yup.string().required('First name is required'), last_name: Yup.string().required('Last name is required'),
  phone_number: Yup.string().required('Phone number is required'), address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'), state: Yup.string().required('State is required'),
  zip_code: Yup.string().required('ZIP code is required'), country: Yup.string().required('Country is required'),
  gender: Yup.string().required('Gender is required'), language: Yup.string().required('Language is required'),
});
const getResults = (response) => response?.data?.results || response?.data?.data?.results || [];
const apiError = (error) => {
  const message = error?.message || error?.response?.data?.message;
  if (typeof message === 'string') return message;
  if (message && typeof message === 'object') return Object.values(message).flat().join(' ');
  return 'Account could not be created.';
};

export default function CompanyDiscovery() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [zipCode, setZipCode] = useState('');
  const [companies, setCompanies] = useState([]);
  const [provider, setProvider] = useState('');
  const [account, setAccount] = useState(initialAccount);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const searchCompanies = async () => {
    if (!zipCode.trim()) return;
    setLoading(true); setError('');
    try {
      const response = await axios.get(PUBLIC_COMPANIES, { params: { zip_code: zipCode.trim() } });
      const results = getResults(response);
      setCompanies(results); setSearched(true);
      setProvider(results.length === 1 ? results[0].provider : '');
    } catch (requestError) {
      setCompanies([]); setSearched(true); setError(apiError(requestError));
    } finally { setLoading(false); }
  };

  const continueWithCompany = () => {
    if (!provider) return;
    saveActiveProvider(provider); setError(''); setStep(2);
  };

  const submitProfile = async (values, helpers) => {
    const { confirmPassword, remember, ...credentials } = account;
    setError('');
    try {
      saveActiveProvider(provider);
      const response = await axios.post(DIRECT_CUSTOMER_SIGNUP, { ...credentials, ...values }, { headers: { 'Content-Type': 'application/json' } });
      register(response.data);
      navigate('/dashboard/default', { replace: true });
    } catch (requestError) { setError(apiError(requestError)); }
    finally { helpers.setSubmitting(false); }
  };

  return <SignupShell><Card className="signup-card"><Grid container sx={{ height: '100%' }}>
    <Grid item sm={5} xs={12} sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', p: 4, bgcolor: 'rgba(0,0,0,.01)' }}>
      <Box sx={{ textAlign: 'center' }}><Box component="img" width="100%" alt="Register" src="/assets/images/illustrations/posting_photo.svg" /><Typography sx={{ mt: 2, color: '#52647d', fontSize: 13 }}>Find your local company and create your customer account.</Typography></Box>
    </Grid>
    <Grid item sm={7} xs={12} sx={{ height: '100%', overflowY: 'auto', p: { xs: 3, sm: 4 } }}>
      <Box component="img" src={logo} alt="Cleany" className="logo" />
      <Typography sx={{ mb: .5, fontSize: 20, fontWeight: 800 }}>{step === 1 ? 'Choose your company' : step === 2 ? 'Create your account' : 'Complete your profile'}</Typography>
      <Typography sx={{ mb: 2.5, color: '#64748b', fontSize: 13 }}>Step {step} of 3</Typography>
      <LinearProgress variant="determinate" value={(step / 3) * 100} sx={{ mb: 3, height: 7, borderRadius: 4 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {step === 1 && <Box><Box sx={{ display: 'flex', gap: 1 }}>
        <TextField fullWidth size="small" label="ZIP code" value={zipCode} onChange={(event) => setZipCode(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); searchCompanies(); } }} />
        <LoadingButton variant="contained" loading={loading} disabled={!zipCode.trim()} onClick={searchCompanies}>Search</LoadingButton>
      </Box>
        {searched && companies.length > 0 && <TextField select fullWidth size="small" label="Select company" value={provider} onChange={(event) => setProvider(event.target.value)} sx={{ mt: 2 }}>{companies.map((company) => <MenuItem key={company.provider} value={company.provider}><Box><Typography sx={{ fontSize: 14, fontWeight: 700 }}>{company.title}</Typography><Typography sx={{ color: '#64748b', fontSize: 11 }}>{[company.city, company.state].filter(Boolean).join(', ')}</Typography></Box></MenuItem>)}</TextField>}
        {searched && companies.length === 0 && !error && <Alert severity="info" sx={{ mt: 2 }}>No company is available for this ZIP code.</Alert>}
        <Button fullWidth variant="contained" disabled={!provider} onClick={continueWithCompany} sx={{ mt: 3 }}>Next</Button>
      </Box>}

      {step === 2 && <Formik initialValues={account} validationSchema={accountSchema} onSubmit={(values) => { setAccount(values); setStep(3); }}>{({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => <Box component="form" onSubmit={handleSubmit}>
        {['email', 'password', 'confirmPassword'].map((name) => <TextField key={name} fullWidth required size="small" name={name} type={name === 'email' ? 'email' : 'password'} label={name === 'confirmPassword' ? 'Confirm password' : name.charAt(0).toUpperCase() + name.slice(1)} value={values[name]} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched[name] && errors[name])} helperText={touched[name] && errors[name]} sx={{ mb: 2 }} />)}
        <Box sx={{ display: 'flex', alignItems: 'center' }}><Checkbox size="small" name="remember" checked={values.remember} onChange={handleChange} /><Typography sx={{ fontSize: 13 }}>I have read and agree to the terms and policy.</Typography></Box>
        <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}><Button variant="outlined" onClick={() => setStep(1)}>Back</Button><Button fullWidth type="submit" variant="contained">Next</Button></Box>
      </Box>}</Formik>}

      {step === 3 && <Formik initialValues={{ ...initialProfile, zip_code: zipCode }} validationSchema={profileSchema} onSubmit={submitProfile}>{({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, setFieldTouched }) => <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>{[['first_name', 'First name'], ['last_name', 'Last name'], ['phone_number', 'Phone number']].map(([name, label]) => <Grid item xs={12} sm={6} key={name}><TextField fullWidth required size="small" name={name} label={label} value={values[name]} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched[name] && errors[name])} helperText={touched[name] && errors[name]} /></Grid>)}
          <Grid item xs={12}>
            <GooglePlacesAutocomplete apiKey="AIzaSyD7Y6D1Vbeyy6_-2nYEpNdqUaLoXaf7EXs" selectProps={{
              inputId: 'direct-signup-address', placeholder: 'Search street address...',
              onBlur: () => setFieldTouched('address', true),
              onChange: async (place) => {
                setFieldValue('address', place?.label || '');
                setFieldValue('city', ''); setFieldValue('state', ''); setFieldValue('zip_code', '');
                setFieldValue('latitude', ''); setFieldValue('longitude', '');
                if (!place?.label) return;
                try {
                  const results = await geocodeByAddress(place.label);
                  const result = results[0];
                  const coordinates = await getLatLng(result);
                  const components = result?.address_components || [];
                  const part = (types, short = false) => {
                    const component = components.find((item) => types.some((type) => item.types?.includes(type)));
                    return component?.[short ? 'short_name' : 'long_name'] || '';
                  };
                  setFieldValue('address', result?.formatted_address || place.label);
                  setFieldValue('city', part(['locality', 'postal_town', 'sublocality']));
                  setFieldValue('state', part(['administrative_area_level_1'], true));
                  setFieldValue('zip_code', part(['postal_code']));
                  setFieldValue('country', part(['country']) || values.country);
                  setFieldValue('latitude', coordinates.lat);
                  setFieldValue('longitude', coordinates.lng);
                } catch (addressError) {
                  setError('The selected address could not be resolved. Please select another suggestion.');
                }
              },
              styles: {
                control: (provided, state) => ({ ...provided, minHeight: 40, borderColor: touched.address && errors.address ? '#d32f2f' : state.isFocused ? '#1976d2' : '#c4c4c4', boxShadow: state.isFocused ? '0 0 0 1px #1976d2' : 'none' }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              },
            }} />
            {touched.address && errors.address && <Typography sx={{ mt: .5, ml: 1.75, color: 'error.main', fontSize: 12 }}>{errors.address}</Typography>}
          </Grid>
          {[['city', 'City'], ['state', 'State'], ['zip_code', 'ZIP code'], ['country', 'Country']].map(([name, label]) => <Grid item xs={12} sm={6} key={name}><TextField fullWidth required size="small" name={name} label={label} value={values[name]} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched[name] && errors[name])} helperText={touched[name] && errors[name]} /></Grid>)}
          <Grid item xs={12} sm={6}><TextField select fullWidth size="small" name="gender" label="Gender" value={values.gender} onChange={handleChange}><MenuItem value="Unspecified">Unspecified</MenuItem><MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem></TextField></Grid>
          <Grid item xs={12} sm={6}><TextField select fullWidth size="small" name="language" label="Language" value={values.language} onChange={handleChange}><MenuItem value="english">English</MenuItem><MenuItem value="french">French</MenuItem></TextField></Grid>
        </Grid>
        <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}><Button variant="outlined" onClick={() => setStep(2)}>Back</Button><LoadingButton fullWidth type="submit" variant="contained" loading={isSubmitting}>Create new account</LoadingButton></Box>
      </Box>}</Formik>}
      <Typography sx={{ mt: 2.5, fontSize: 13 }}>Already have an account? <NavLink to="/session/signin">Login</NavLink></Typography>
    </Grid>
  </Grid></Card></SignupShell>;
}
